<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: What did each account cost or earn, month by month or week by week - and what
            does an account's own branch break down into?
  Answer:   the table itself - one row per period, one column per account, an account
            column opening into its children on demand, a total column closing each row.
  Context:  the class/bucket/date/currency the table was built for.
  Controls: the class, bucket and date filters, persisted in the URL, beneath the Reports tab strip.
-->
<script setup lang="ts">
import { ErrorState } from '@taler/ui'
import { formatLocalDate, systemClock, todayUtc } from '@taler/domain'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  BucketSelect,
  canViewReporting,
  DateStepper,
  type ParentRollupQuery,
  PeriodRollupClassSelect,
  PeriodRollupTable,
  ReportDateField,
  ReportFrame,
  usePeriodRollup,
  usePeriodRollupQueryState,
} from '@/features/reporting'
import { useDisplayCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'

const { t, locale } = useI18n()
const session = useSession()
const today = () => formatLocalDate(todayUtc(systemClock))
const queryState = usePeriodRollupQueryState(today)
const { displayCurrency } = useDisplayCurrency()

const canView = computed(() => canViewReporting(session.can))

// Client-only view state: which account columns are currently expanded. Reset whenever
// the class changes, since an expanded id from one class is meaningless in the other.
const expanded = ref(new Set<string>())
function toggle(accountId: string): void {
  const next = new Set(expanded.value)
  if (next.has(accountId)) next.delete(accountId)
  else next.add(accountId)
  expanded.value = next
}

function setClass(next: ParentRollupQuery['class']): void {
  expanded.value = new Set()
  queryState.setFilter({ class: next })
}

const rollup = usePeriodRollup({
  ledgerId: () => session.activeLedger.value?.id,
  query: () => ({ ...queryState.query.value, displayCurrency: displayCurrency.value }),
  expanded: () => expanded.value,
  locale: () => locale.value as 'en' | 'hu',
  enabled: () => canView.value,
})
</script>

<template>
  <ReportFrame :title="t('reporting.title')" :subtitle="t('reporting.overview.subtitle')">
    <template v-if="canView && session.activeLedger.value !== undefined" #filters>
      <div class="flex flex-wrap items-end gap-[var(--space-group)]">
        <PeriodRollupClassSelect :model-value="queryState.query.value.class" @update:model-value="setClass" />
        <BucketSelect
          :model-value="queryState.query.value.bucket"
          @update:model-value="queryState.setFilter({ bucket: $event })"
        />
        <ReportDateField
          :label="t('reporting.from')"
          :model-value="queryState.query.value.from ?? ''"
          :max="today()"
          @update:model-value="queryState.setFilter({ from: $event || undefined })"
        />
        <ReportDateField
          :label="t('reporting.until')"
          :model-value="queryState.query.value.until"
          :max="today()"
          @update:model-value="queryState.setFilter({ until: $event })"
        />
        <span
          aria-hidden="true"
          class="mx-1 h-[var(--density-control)] w-px bg-[color:var(--color-border)]"
        />
        <DateStepper
          :model-value="queryState.query.value.until"
          :max="today()"
          @update:model-value="queryState.setFilter({ until: $event })"
        />
      </div>
    </template>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noLedger') }}
    </p>
    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">{{ t('reporting.noAccess') }}</p>

    <template v-else>
      <p v-if="rollup.isLoading.value" role="status">{{ t('reporting.loading') }}</p>

      <ErrorState
        v-else-if="rollup.error.value"
        :title="t('reporting.errorTitle')"
        :retry-label="t('reporting.retry')"
        :retrying="rollup.retrying.value"
        @retry="rollup.retry()"
      />

      <PeriodRollupTable
        v-else-if="rollup.table.value"
        :data="rollup.table.value"
        :bucket="queryState.query.value.bucket"
        :account-class="queryState.query.value.class"
        @toggle="toggle"
      />
    </template>
  </ReportFrame>
</template>
